<template>
  <v-navigation-drawer
    v-model="isMobileMenuDrawerOpened"
    location="right"
    :class="{ '!w-[100vw]': isMobileMenuDrawerOpened }"
  >
    <div class="flex flex-col h-full">
      <VRow v-if="canListPatients" class="m-4 flex-grow-0">
        <PatientSearch />
      </VRow>

      <VRow v-if="isConnectEnabled" class="m-2 flex-grow-0">
        <VBtn
          :to="{ name: routeNames.CONSULTATIONS }"
          :active="false"
          class="button-large p-2 w-full justify-start"
          variant="text"
          :class="{
            'text-primary bg-[#F5F5F7]': isConsultationsActive,
            'text-secondary-text': !isConsultationsActive
          }"
          data-testid="menu-consultations-button"
        >
          <template #prepend>
            <VIcon icon="calendar_month" class="text-mobile-menu-icon opacity-55" />
          </template>
          <span class="text-h6 ml-2">
            {{ t('Consultations') }}
          </span>
        </VBtn>
      </VRow>

      <VRow v-if="canCreateConsultations" class="m-2 flex-grow-0">
        <VBtn
          :to="{ name: routeNames.VITAL_DATA }"
          :active="false"
          class="button-large p-2 w-full justify-start"
          :class="{
            'text-primary bg-[#F5F5F7]': isVitalDataActive,
            'text-secondary-text': !isVitalDataActive
          }"
          variant="text"
        >
          <template #prepend>
            <VIcon icon="custom:patientList" class="text-mobile-menu-icon opacity-55" />
          </template>
          <span class="text-h6 ml-2">
            {{ t('Patient Overview') }}
          </span>
        </VBtn>
      </VRow>

      <DividerLine />

      <VRow v-if="canCreateConsultations && isConnectEnabled" class="m-2 flex-grow-0">
        <VBtn
          :active="false"
          :disabled="!isEmergencyRoutingAvailable"
          class="button-large p-2 w-full justify-start"
          color="secondary"
          variant="text"
          data-testid="emergency-button"
          @click="handleOpenEmergencyDialog"
        >
          <template #prepend>
            <VIcon icon="emergency" class="material-icons-round" />
          </template>
          <span class="ml-2">
            {{ t('Emergency Call') }}
          </span>
        </VBtn>
      </VRow>

      <DividerLine />

      <VRow class="m-2 flex-grow-0">
        <VBtn
          :to="{ name: routeNames.USER_PROFILE }"
          :active="false"
          class="button-large p-2 w-full justify-start"
          variant="text"
        >
          <template #prepend>
            <VIcon icon="person" class="text-mobile-menu-icon opacity-55" />
          </template>
          <span class="text-h6 text-secondary-text ml-2">
            {{ t('Account') }}
          </span>
        </VBtn>
      </VRow>

      <VRow v-if="isHandoverAvailable" class="m-2 flex-grow-0">
        <VBtn
          :to="{ name: routeNames.LOGOUT_HANDOVER }"
          :active="false"
          class="button-large p-2 w-full justify-start"
          variant="text"
          data-testid="menu-handover-button"
        >
          <template #prepend>
            <VIcon icon="turn_right" class="text-mobile-menu-icon opacity-55" />
          </template>
          <span class="text-h6 text-secondary-text ml-2">
            {{ t('Consultation Handover') }}
          </span>
        </VBtn>
      </VRow>

      <VSpacer />
      <VRow class="m-4 flex-grow-0 flex flex-col gap-4">
        <div class="flex flex-col">
          <span class="text-caption text-secondary-text">{{ t('Logged in as') }}</span>
          <span class="text-body-1 text-primary-text">{{ fullUserName }}</span>
        </div>

        <div v-if="isConnectEnabled && showShiftIndicator" class="flex-col flex">
          <span class="text-caption text-secondary-text mb-1">{{ t('Shift') }}</span>
          <ShiftStatusIndicator disable-tooltip />
        </div>

        <VBtn color="primary" class="button-large w-full !h-11" @click="handleOpenLogoutModal">
          {{ t('Logout') }}
        </VBtn>

        <LogoutModal v-model="isLogoutModalOpen" />
      </VRow>
    </div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { routeNames } from '@/router/routeNames'
import PatientSearch from '@/components/navigation/patientsearch/PatientSearch.vue'
import ShiftStatusIndicator from '@/components/navigation/ShiftStatusIndicator.vue'
import { useI18n } from 'vue-i18n'
import { computed, inject, onMounted, onUnmounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import DividerLine from '@/atoms/DividerLine.vue'
import { useRouter } from 'vue-router'
import type { OpenEmergencyModalFn } from '@/types/OpenEmergencyModalFn'
import useRoutingAvailability from '@/composables/useRoutingAvailability'
import useHandoverAvailability from '@/composables/useHandoverAvailability'
import { usePluginsStore } from '@/stores/plugins'
import { ref } from 'vue'
import LogoutModal from '@/components/navigation/LogoutModal.vue'

interface Props {
  showShiftIndicator: boolean
  modelValue: boolean
}

const router = useRouter()

const { t } = useI18n()
const { isEmergencyRoutingAvailable } = useRoutingAvailability()
const { isHandoverAvailable } = useHandoverAvailability()

const openEmergencyModal = inject<OpenEmergencyModalFn>('openEmergencyModal', () => {})

defineProps<Props>()

const isMobileMenuDrawerOpened = defineModel<boolean>()
const isLogoutModalOpen = ref(false)

const { canCreateConsultations, canListPatients } = storeToRefs(useUserStore())
const { isConnectEnabled } = storeToRefs(usePluginsStore())

const fullUserName = computed(() => {
  const title = useUserStore().user?.title || ''
  const firstName = useUserStore().user?.firstName || ''
  const lastName = useUserStore().user?.lastName || ''

  return `${title} ${firstName} ${lastName}`
})
const isVitalDataActive = computed(() => router.currentRoute.value.name === routeNames.VITAL_DATA)
const isConsultationsActive = computed(
  () => router.currentRoute.value.name === routeNames.CONSULTATIONS
)

function handleOpenEmergencyDialog() {
  isMobileMenuDrawerOpened.value = false
  openEmergencyModal()
}

function handleWindowWidthChange() {
  if (window.innerWidth >= 1024) {
    isMobileMenuDrawerOpened.value = false
  }
}

function handleOpenLogoutModal() {
  isMobileMenuDrawerOpened.value = false
  isLogoutModalOpen.value = true
}

onMounted(() => {
  window.addEventListener('resize', handleWindowWidthChange)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleWindowWidthChange)
})
</script>

<style scoped></style>
